"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export function useSnapCarousel(count: number, initialIndex = 0) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(initialIndex);

  const setItemRef = useCallback(
    (i: number) => (el: HTMLDivElement | null) => {
      itemRefs.current[i] = el;
    },
    []
  );

  useEffect(() => {
    if (initialIndex > 0 && containerRef.current && itemRefs.current[initialIndex]) {
      const container = containerRef.current;
      const target = itemRefs.current[initialIndex];
      if (container && target) {
        const left =
          target.offsetLeft - container.offsetWidth / 2 + target.offsetWidth / 2;
        container.scrollLeft = left;
      }
    }
  }, [initialIndex]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let best = { ratio: 0, index: -1 };
        entries.forEach((entry) => {
          const idx = itemRefs.current.findIndex((el) => el === entry.target);
          if (idx !== -1 && entry.intersectionRatio > best.ratio) {
            best = { ratio: entry.intersectionRatio, index: idx };
          }
        });
        if (best.index !== -1) {
          setActive(best.index);
        }
      },
      { root: container, threshold: [0.5, 0.6, 0.75, 0.9] }
    );

    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [count]);

  const scrollTo = useCallback((index: number) => {
    const container = containerRef.current;
    const target = itemRefs.current[index];
    if (container && target) {
      const left =
        target.offsetLeft - container.offsetWidth / 2 + target.offsetWidth / 2;
      container.scrollTo({
        left,
        behavior: "smooth",
      });
    }
  }, []);

  return { containerRef, setItemRef, active, scrollTo };
}
